import React, { useMemo } from 'react';
import { styled } from '@compiled/react';
import { Text, Box, xcss, Stack } from '@atlaskit/primitives';
import type { OptionProps, OptionType } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import type { CmdbObject } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/cmdb-object.tsx';
import { MemoizedInsightIcon } from '@atlassian/jira-servicedesk-insight-icon/src/ui/index.tsx';
import type { CmdbObjectAttribute } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/attribute.tsx';
import { fg } from '@atlassian/jira-feature-gating';

export type Props = OptionProps<OptionType, boolean> & {
	attributesToDisplayInSearch: string[];
};

type BoldProps = {
	match: string;
	children: string;
};

export const BoldMatchingText = ({ match, children }: BoldProps): React.JSX.Element => {
	if (match === '') {
		return <>{children}</>;
	}
	// we need to escape special characters to avoid getting them interpreted in the RegExp group
	const escapedSpecialCharsInMatchQuery = match.replace(/[\\.,*+?^${}()|[\]]/g, '\\$&');
	const parts = children.split(new RegExp(`(${escapedSpecialCharsInMatchQuery})`, 'gi'));
	return (
		<>
			{parts.map((part, i) => {
				if (part.toLowerCase() === match.toLowerCase()) {
					return (
						<Text as="strong" key={i}>
							{part}
						</Text>
					);
				}
				return <React.Fragment key={i}>{part}</React.Fragment>;
			})}
		</>
	);
};

export const createAttributeString = (attributes: CmdbObjectAttribute[]): string =>
	attributes
		.map(({ objectAttributeValues }) =>
			objectAttributeValues.map(({ displayValue }) => displayValue).join(', '),
		)
		.join(' | ');

// Need to be careful when change this component
// The usage in CmdbEditView.tsx did not pass the whole object in data.rawData since it used graphql
export const CmdbSelectOption = ({
	attributesToDisplayInSearch,
	data,
	selectProps,
	label,
}: Props): React.JSX.Element => {
	// Casting to CmdbObject as typing gets lost when passed through AK Select
	const object: CmdbObject = data.rawData;
	const { inputValue } = selectProps;
	const displayedAttributes = useMemo(
		() =>
			attributesToDisplayInSearch
				.map((attrName) =>
					attrName === 'Label'
						? object.attributes.find((attr) => attr.objectTypeAttribute.label)
						: object.attributes.find((attr) => attr.objectTypeAttribute.name === attrName),
				)
				.filter(Boolean),
		[object, attributesToDisplayInSearch],
	);
	const primaryAttributeString =
		displayedAttributes?.[0]?.objectAttributeValues
			// @ts-expect-error - TS2349 - This expression is not callable.
			?.filter((v: any) => v.displayValue) // eslint-disable-line @typescript-eslint/no-explicit-any
			.map((v: any) => v.displayValue) // eslint-disable-line @typescript-eslint/no-explicit-any
			.join(', ') || label;
	const attributesString = createAttributeString(displayedAttributes.slice(1));
	return fg('modern_issue_transition_field_support_cmdb') ? (
		// Fix double hover issue and transformed to Box and Stack to avoid using styled-components
		<Box xcss={outerBoxStyles} testId="servicedesk-cmdb-field-object-picker.ui.cmdb-select-option">
			<Box xcss={iconWrapperStyles}>
				<MemoizedInsightIcon
					mediaClientConfig={object.avatar.mediaClientConfig}
					iconUrl={object.avatar.url48}
					label={object.label}
					size="small"
				/>
			</Box>
			<Stack>
				<Box
					xcss={objectNameStyles}
					testId="servicedesk-cmdb-field-object-picker.ui.cmdb-select-option.object-name"
				>
					<BoldMatchingText match={inputValue ?? ''}>{primaryAttributeString}</BoldMatchingText>
				</Box>
				{attributesString && (
					<Box
						xcss={attributesStyles}
						testId="servicedesk-cmdb-field-object-picker.ui.cmdb-select-option.attributes"
					>
						<BoldMatchingText match={inputValue ?? ''}>{attributesString}</BoldMatchingText>
					</Box>
				)}
			</Stack>
		</Box>
	) : (
		<Outer data-testid="servicedesk-cmdb-field-object-picker.ui.cmdb-select-option">
			<IconWrapper>
				<MemoizedInsightIcon
					mediaClientConfig={object.avatar.mediaClientConfig}
					iconUrl={object.avatar.url48}
					label={object.label}
					size="small"
				/>
			</IconWrapper>
			<div>
				<ObjectName data-testid="servicedesk-cmdb-field-object-picker.ui.cmdb-select-option.object-name">
					<BoldMatchingText match={inputValue ?? ''}>{primaryAttributeString}</BoldMatchingText>
				</ObjectName>
				{attributesString && (
					<Attributes data-testid="servicedesk-cmdb-field-object-picker.ui.cmdb-select-option.attributes">
						<BoldMatchingText match={inputValue ?? ''}>{attributesString}</BoldMatchingText>
					</Attributes>
				)}
			</div>
		</Outer>
	);
};
const outerBoxStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	paddingTop: 'space.100',
	paddingRight: 'space.200',
	paddingBottom: 'space.100',
	paddingLeft: 'space.200',
});

const iconWrapperStyles = xcss({
	marginRight: 'space.150',
	flexShrink: 0,
});

const objectNameStyles = xcss({
	font: 'font.body',
});

const attributesStyles = xcss({
	font: 'font.body.UNSAFE_small',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Outer = styled.div({
	display: 'flex',
	alignItems: 'center',
	paddingTop: token('space.100'),
	paddingRight: token('space.200'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.200'),
	'&:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div({
	marginRight: token('space.150'),
	flexShrink: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ObjectName = styled.div({
	font: token('font.body'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '24px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Attributes = styled.div({
	font: token('font.body.UNSAFE_small'),
});
